import { Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import {
  CorteBlocoApi,
  CorteDevolucaoDados,
  CorteExcluirEvento,
  CorteItem,
  CorteLinhaGrid,
  CorteService,
  ProdutoCorteInfo,
  ResumoCorte,
} from "../../services/corte.service";
import {
  CorteDevolucaoAcao,
} from "../corte-devolucao-controle/corte-devolucao-controle.component";

@Component({
  selector: "app-corte-resumo",
  templateUrl: "./corte-resumo.component.html",
  styleUrls: ["./corte-resumo.component.css"],
})
export class CorteResumoComponent implements OnChanges {
  @Input() itens: CorteItem[] = [];
  @Input() editandoId: string | null = null;
  @Input() produto: ProdutoCorteInfo | null = null;
  @Input() productDetalhe: any = null;
  @Input() precoTabela: number | null = null;
  @Input() bloqueado = false;
  @Input() corteBloco: CorteBlocoApi | null = null;
  @Output() editar = new EventEmitter<string>();
  @Output() excluir = new EventEmitter<CorteExcluirEvento>();
  @Output() devolucaoAcao = new EventEmitter<CorteDevolucaoAcao>();
  /** Quando há `corte.barras` na API e `itens` ainda vazio no pai. */
  @Output() itensDerivados = new EventEmitter<CorteItem[]>();

  constructor(private corte: CorteService) {}

  resumo: ResumoCorte = {
    totalCortes: 0,
    totalSolicitado: 0,
    pesoTotal: 0,
    sobraTotal: 0,
    totalFinal: 0,
  };

  ngOnChanges(): void {
    this.sincronizarItensDerivadosSeNecessario();
    const calc = this.corte.calcularResumo(this.itens, this.produto);
    if (this.corteBloco) {
      this.resumo = this.corte.aplicarResumoCorteApi(calc, this.corteBloco, this.produto);
      return;
    }
    // Sem bloco da API: só estimativa local (metros × fator) até o POST retornar peso_total_liquido.
    this.resumo = calc;
  }

  /** Repassa ao pai os itens inferidos de `corte.barras` para habilitar editar/excluir. */
  private sincronizarItensDerivadosSeNecessario(): void {
    if (this.itens.length > 0 || !this.corteBloco) {
      return;
    }
    const derivados = this.corte.itensDeBlocoCorte(this.corteBloco);
    if (derivados.length > 0) {
      this.itensDerivados.emit(derivados);
    }
  }

  get dadosDevolucao(): CorteDevolucaoDados {
    if (this.corteBloco) {
      return this.corte.extrairDadosDevolucao(this.corteBloco, this.productDetalhe);
    }
    return {
      sobraTotal: this.resumo.sobraTotal,
      sobraLiquida: this.resumo.sobraLiquida ?? this.resumo.sobraTotal,
      sobraElegivel: this.resumo.liquidoElegivel ?? 0,
      sobraNaoElegivel: this.resumo.liquidoNaoElegivel ?? 0,
      devolvido: this.resumo.devolvido ?? 0,
      devolvidoNaoElegivel: 0,
      multiploElegivel: 0,
      multiploNaoElegivel: 0,
    };
  }

  get exibirControleDevolucoes(): boolean {
    return true;
  }

  onDevolucaoAcao(ev: CorteDevolucaoAcao): void {
    this.devolucaoAcao.emit(ev);
  }

  fmt(v: number): string {
    return this.corte.formatarNumero(v);
  }

  fmtFator(v: number): string {
    const n = Number(v ?? 0);
    if (!Number.isFinite(n)) {
      return this.fmt(0);
    }
    return n.toLocaleString("pt-BR", {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  }

  get linhasGrid(): CorteLinhaGrid[] {
    return this.corte.linhasGridCorte(this.corteBloco, this.itens);
  }

  get exibirColunasBarra(): boolean {
    return this.linhasGrid.some((l) => l.barra != null);
  }

  totalLinha(linha: Pick<CorteLinhaGrid, "qtd" | "comprimento_util">): number {
    return this.corte.arredondar2(linha.qtd * linha.comprimento_util);
  }

  podeAcionarLinha(lin: CorteLinhaGrid): boolean {
    if (this.bloqueado) {
      return false;
    }
    return !!this.encontrarItemParaLinha(lin);
  }

  onEditarLinha(lin: CorteLinhaGrid, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    const item = this.encontrarItemParaLinha(lin);
    if (item) {
      this.editar.emit(item.id);
    }
  }

  onExcluirLinha(lin: CorteLinhaGrid, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    const item = this.encontrarItemParaLinha(lin);
    if (!item) {
      return;
    }
    this.excluir.emit({
      id: item.id,
      qtd: lin.qtd,
      comprimento_util: lin.comprimento_util,
      barra: lin.barra,
    });
  }

  private encontrarItemParaLinha(lin: CorteLinhaGrid): CorteItem | undefined {
    return this.corte.encontrarItemParaLinhaGrid(lin, this.itens);
  }

  encontrarItemId(lin: CorteLinhaGrid): string | null {
    return this.encontrarItemParaLinha(lin)?.id ?? null;
  }

  trackLinhaGrid(_index: number, lin: CorteLinhaGrid): string {
    return [
      lin.barra ?? "x",
      lin.comprimento_util,
      lin.qtd,
      lin.itemId ?? "",
    ].join("|");
  }

  get precoUnitarioExibido(): number {
    const api = Number(this.resumo.precoUnitario);
    if (Number.isFinite(api) && api > 0) {
      return api;
    }
    const v = Number(this.precoTabela ?? 0);
    return this.corte.arredondar2(Number.isFinite(v) ? v : 0);
  }

  get pesoExibido(): number {
    const liquido = Number(this.resumo.pesoTotalLiquido);
    if (Number.isFinite(liquido) && liquido > 0) {
      return liquido;
    }
    return Number(this.resumo.pesoTotal ?? 0) || 0;
  }

  get usaPesoLiquidoApi(): boolean {
    const liquido = Number(this.resumo.pesoTotalLiquido);
    return Number.isFinite(liquido) && liquido > 0;
  }

  /** Valor total = `preco_unitario × peso_total_liquido` (API). */
  get valorTotal(): number {
    const api = Number(this.resumo.valorTotal);
    if (Number.isFinite(api) && api >= 0 && api > 0) {
      return api;
    }
    return this.corte.calcularValorTotalCorte(
      this.productDetalhe,
      this.corteBloco,
      this.precoUnitarioExibido,
      this.pesoExibido
    );
  }

  fmtPeso(v: number): string {
    return this.usaPesoLiquidoApi
      ? this.corte.formatarPeso(v)
      : this.fmt(v);
  }

  get mostrarAvisoSobra(): boolean {
    return (
      this.resumo.sobraTotal > 0 &&
      this.produto != null &&
      this.produto.permiteFracionar !== true
    );
  }

  get estoqueKg(): number {
    const v = Number(this.produto?.estoqueKg ?? 0);
    return Number.isFinite(v) ? v : 0;
  }

  get estoqueMt(): number {
    const estoque = this.estoqueKg;
    const uc = Number(this.produto?.unidadeConversao ?? 0);
    if (!Number.isFinite(estoque) || !Number.isFinite(uc) || uc <= 0) {
      return 0;
    }
    return this.corte.arredondar2(estoque / uc);
  }

  get fatorConversaoNumero(): number {
    const v = Number(this.produto?.fatorConversao ?? 0);
    return Number.isFinite(v) ? v : 0;
  }
}
